import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, ChevronDown, ChevronRight, Info, Lock, Plus, Undo2 } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/src/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@repo/ui/src/components/ui/tooltip';
import { cn } from '@repo/ui/src/lib/utils';
import { CLASS_COLORS, type ClassColor, type ClassGroupDetail } from '@repo/shared-types';
import type { ClassDefaultsInput, ClassSlotRowInput, CreateClassInput, UpdateClassInput } from '@repo/validation';
import { ResponsiveSheet, useResponsiveSheetClose } from '../../components/ResponsiveSheet';
import { DatePicker } from '../../components/pickers/DatePicker';
import { useSubjects } from '../../hooks/useSubjects';
import { useBranches } from '../../hooks/useBranches';
import { useMembers } from '../../hooks/useTeam';
import { useShowTeacher, useTeacherOptions } from '../../hooks/useTeachers';
import { useSubscription } from '../../hooks/useSubscription';
import { useClassMode, useOrgSettings } from '../../hooks/useOrgSettings';
import { useAuthStore } from '../../stores/authStore';
import { useClass, useCreateClass, useUpdateClass } from '../../hooks/useClasses';
import { classColorStyle } from '../../lib/classColors';
import { deriveClassName } from '../../lib/classDisplay';
import { formatVnd } from '../../lib/formatVnd';
import { resolveTeacher } from '../../lib/teacherDisplay';
import { todayHouseIso } from '../../lib/houseToday';

// Radix `SelectItem` cannot take `value=""` — same sentinel SlotFormDialog uses.
const NO_BRANCH = '__none__';

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

/** The three "hình thức học" cards (decision 14, reversed: Theo buổi is the
 *  default in BOTH centre kinds). Reuses `EnrollmentMode`'s three values —
 *  same pricingType/billingPeriod pair the enrollment form already derives
 *  them into (`StudentDetailPage`'s `AddEnrollmentForm`) — but this form
 *  owns its own copy under `classForm.*` (the mock's wording for THIS
 *  surface differs from the enrollment dialog's). */
type PricingChoice = 'SESSION_MONTHLY' | 'SESSION_TERM' | 'MONTHLY_FLAT';
const PRICING_CHOICES: readonly PricingChoice[] = ['SESSION_MONTHLY', 'SESSION_TERM', 'MONTHLY_FLAT'];

/** One "Lịch học hàng tuần" row — one per ticked weekday (a class has at
 *  most one khung giờ per weekday in this form's model, matching
 *  `classSlotRowSchema`'s 7-row ceiling). `id` present = an existing
 *  `ClassSlot`; absent = a row this edit session is about to create.
 *  `removed` is the edit-only "bỏ một thứ" soft state (README decision 7):
 *  the row stays in state, struck, until Lưu actually drops it from the
 *  submitted `slots[]` (a row missing from that array is archived). */
interface RowState {
  id?: string;
  dayOfWeek: number;
  startTime: string;
  durationMin: string;
  teacherId: string;
  removed: boolean;
}

type Rows = Partial<Record<number, RowState>>;

function buildRowsFromDetail(detail: ClassGroupDetail): Rows {
  const out: Rows = {};
  for (const s of detail.slots) {
    out[s.dayOfWeek] = {
      id: s.id, dayOfWeek: s.dayOfWeek, startTime: s.startTime,
      durationMin: String(s.durationMin), teacherId: s.teacherId, removed: false,
    };
  }
  return out;
}

/** Whether every row shares one giờ/thời lượng/giáo viên — the disclosure's
 *  initial state on edit (open the per-day table only when there is
 *  something to show that the closed "one Giờ + Thời lượng" view would
 *  otherwise hide, per §6.7 "carry the mock's own decisions", never
 *  invented independently of what the loaded data actually looks like). */
function rowsAreUniform(rows: RowState[]): boolean {
  if (rows.length <= 1) return true;
  const [first, ...rest] = rows;
  return rest.every((r) => r.startTime === first!.startTime && r.durationMin === first!.durationMin && r.teacherId === first!.teacherId);
}

/** A separate component so `useResponsiveSheetClose()` resolves against
 *  `ResponsiveSheet`'s own Provider (wraps `footer` one level below this
 *  component's own render) — same reasoning as `SessionNotesPanel.tsx`'s
 *  `NotesFooterCancelButton`. */
function FooterCancelButton({ disabled, label }: { disabled: boolean; label: string }) {
  const { requestClose } = useResponsiveSheetClose();
  return (
    <Button type="button" variant="outline" className="h-[33.6px] md:h-[33.6px]" onClick={requestClose} disabled={disabled}>
      {label}
    </Button>
  );
}

interface FormErrorState {
  message: string;
  /** Which element the message/tint anchors under. `row` matches by
   *  dayOfWeek+startTime (what `SLOT_TIME_CONFLICT` names) rather than by
   *  React key, since the offending row can be either an existing slot or a
   *  row this submit was about to create. */
  target: { kind: 'row'; dayOfWeek: number; startTime: string } | { kind: 'startsOn' } | { kind: 'generic' };
}

/**
 * THI-267 Task 11 — S4: Tạo lớp / Sửa lớp, ported from
 * `docs/design/design_handoff_class_layer/S4 - Form Tao lop.dc.html`
 * (frames 1-4, §6.7). `classMode` (COHORT/INDIVIDUAL, `useOrgSettings`)
 * picks the CREATE variant; EDIT is always the full form with Môn locked.
 *
 * Fix wave (review I3): `GET /api/classes/:id` (`ClassGroupDetail`) DOES
 * carry `room` and `defaults.*` (added mid-cycle, commit `2020e5c4`) — the
 * edit branch below seeds Phòng and Section 4 from them like every other
 * field, and every edit resend of `defaults`/`room` reflects the form's own
 * true current state (seeded-then-possibly-changed), so there is no more
 * "untouched means omit" workaround: `buildDefaults()` runs the same way on
 * create and edit. `branchId` is the one field still gated on
 * `branchTouched` — the per-slot `branchId` a class's slots can carry isn't
 * summarised anywhere on the wire beyond one representative guess, so an
 * untouched Chi nhánh select must still stay out of the payload rather than
 * collapse genuinely different per-slot branches down to one value.
 */
export function ClassFormSheet({
  open, onOpenChange, classId, defaultSubjectId, onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** When provided, the sheet edits this class instead of creating a new
   *  one — always the full (COHORT-shaped) form regardless of `classMode`,
   *  with Môn locked (spec §2 decision 2 / THI-262). */
  classId?: string;
  /** Create-only: prefills Môn học when the caller already knows the
   *  subject (e.g. a future "Tạo lớp mới" reached from a subject-scoped
   *  context) — still editable. */
  defaultSubjectId?: string;
  onSaved?: (detail: ClassGroupDetail) => void;
}) {
  const { t } = useTranslation('scheduling');
  const classMode = useClassMode();
  const { data: subjects } = useSubjects();
  const { data: branchList } = useBranches({ includeArchived: true });
  const allBranches = branchList?.branches;
  const showTeacher = useShowTeacher();
  const teacherOptions = useTeacherOptions();
  const { data: members } = useMembers();
  const { data: membersInclRemoved } = useMembers({ includeRemoved: true });
  const { data: subscription } = useSubscription();
  const { data: orgSettings } = useOrgSettings();
  const authUser = useAuthStore((s) => s.user);

  const isEdit = !!classId;
  const { data: detail } = useClass(classId ?? null);
  const detailReady = !isEdit || (detail && detail.id === classId);
  // COHORT gets the full 5-section form; INDIVIDUAL gets the collapsed one
  // — but ONLY on create. On edit it is always the full form (resolutions).
  const isCollapsed = !isEdit && classMode === 'INDIVIDUAL';

  const activeBranches = (allBranches ?? []).filter((b) => !b.archivedAt);
  const allowsBranches = subscription?.plan.allowsBranches ?? false;
  const showBranchField = activeBranches.length > 0 && allowsBranches;

  const defaultTeacherId = members?.find((m) => m.user.id === authUser?.id)?.id ?? '';

  // ── Local state — blank/create defaults; the render-time blocks below
  // reset on open and seed from `detail` once it has loaded for `classId`
  // (the "adjust state during render" pattern ResponsiveSheet/AssignDialog
  // already use: a lazy `useState` initializer only runs once at MOUNT, but
  // `detail` arrives asynchronously after this component is already
  // mounted, so a plain initializer cannot seed it — see the long comment
  // below the state block). ─────────────────────────────────────────────
  const [name, setName] = useState('');
  const [subjectId, setSubjectId] = useState(defaultSubjectId ?? '');
  const [topTeacherId, setTopTeacherId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [branchTouched, setBranchTouched] = useState(false);
  const [room, setRoom] = useState('');
  const [maxCapacity, setMaxCapacity] = useState('');
  const [rows, setRows] = useState<Rows>({});
  const [disclosureOpen, setDisclosureOpen] = useState(false);
  const [commonStartTime, setCommonStartTime] = useState('18:00');
  const [commonDurationMin, setCommonDurationMin] = useState('60');
  const [startsOn, setStartsOn] = useState(todayHouseIso());
  const [hasEndDate, setHasEndDate] = useState(false);
  const [endsOn, setEndsOn] = useState('');
  const [pricingChoice, setPricingChoice] = useState<PricingChoice>('SESSION_MONTHLY');
  const [rate, setRate] = useState('');
  const [termSize, setTermSize] = useState('');
  const [chargeAbsent, setChargeAbsentState] = useState(orgSettings?.defaultChargeAbsent ?? false);
  const [color, setColor] = useState<ClassColor | null>(null);
  const [pricingExpanded, setPricingExpanded] = useState(false);
  const [formError, setFormError] = useState<FormErrorState | null>(null);
  const [dirty, setDirty] = useState(false);

  const markDirty = () => setDirty(true);
  const clearError = () => { if (formError) setFormError(null); };

  // Reset-on-open (create) / reseed-on-open-or-load (edit): the React-
  // documented "adjust state during render" technique — comparing a prop
  // (or query result) to its own last-seen value stored in state, then
  // conditionally calling setters in the render body itself — rather than
  // an effect, so the very first render after `open` flips true (or after
  // `detail` arrives) already shows the right values instead of flashing
  // the previous session's for one frame (same reasoning ResponsiveSheet's
  // own `openEdge`/`lockedIsDesktop` comment gives).
  const [prevOpen, setPrevOpen] = useState(open);
  const [seededForId, setSeededForId] = useState<string | null>(null);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open && !isEdit) {
      // Fresh create: blank everything (in case this same mounted instance
      // was previously used for a different open — ClassesPage's own usage
      // never re-triggers this, but a future caller reusing one instance
      // across opens must not leak the last session's draft).
      setName(''); setSubjectId(defaultSubjectId ?? ''); setTopTeacherId(defaultTeacherId);
      setBranchId(''); setBranchTouched(false); setRoom('');
      setMaxCapacity(''); setRows({}); setDisclosureOpen(false);
      setCommonStartTime('18:00'); setCommonDurationMin('60'); setStartsOn(todayHouseIso());
      setHasEndDate(false); setEndsOn(''); setPricingChoice('SESSION_MONTHLY');
      setRate(''); setTermSize('');
      setChargeAbsentState(orgSettings?.defaultChargeAbsent ?? false);
      setColor(null); setPricingExpanded(false); setFormError(null); setDirty(false);
      setSeededForId(null);
    }
  }
  if (isEdit && detail && detail.id === classId && seededForId !== detail.id) {
    setSeededForId(detail.id);
    const detailRows = buildRowsFromDetail(detail);
    const activeRowList = Object.values(detailRows).filter((r): r is RowState => !!r);
    setName(detail.name ?? '');
    setRows(detailRows);
    setDisclosureOpen(!rowsAreUniform(activeRowList));
    const first = activeRowList[0];
    setCommonStartTime(first?.startTime ?? '18:00');
    setCommonDurationMin(first?.durationMin ?? '60');
    setStartsOn(detail.startsOn.slice(0, 10));
    setHasEndDate(!!detail.endsOn);
    setEndsOn(detail.endsOn ? detail.endsOn.slice(0, 10) : '');
    setMaxCapacity(detail.maxCapacity != null ? String(detail.maxCapacity) : '');
    setColor(detail.color);
    const repBranch = first ? (allBranches ?? []).find((b) => b.id === (detail.slots.find((s) => s.id === first.id)?.branchId ?? null))?.id : undefined;
    setBranchId(repBranch ?? '');
    setBranchTouched(false);
    setRoom(detail.room ?? '');
    const seededPricingChoice: PricingChoice = detail.defaults.pricingType === 'MONTHLY_FLAT'
      ? 'MONTHLY_FLAT'
      : detail.defaults.billingPeriod === 'TERM' ? 'SESSION_TERM' : 'SESSION_MONTHLY';
    setPricingChoice(seededPricingChoice);
    const seededRate = seededPricingChoice === 'MONTHLY_FLAT' ? detail.defaults.monthlyRate : detail.defaults.ratePerSession;
    setRate(seededRate != null ? String(seededRate) : '');
    setTermSize(detail.defaults.termSize != null ? String(detail.defaults.termSize) : '');
    setChargeAbsentState(detail.defaults.chargeAbsent ?? (orgSettings?.defaultChargeAbsent ?? false));
    setPricingExpanded(false); setFormError(null); setDirty(false);
  }

  const subject = subjects?.find((s) => s.id === subjectId);
  const activeRows = WEEKDAYS.map((d) => rows[d]).filter((r): r is RowState => !!r);
  const liveRows = activeRows.filter((r) => !r.removed);
  const tableDays = WEEKDAYS.filter((d) => rows[d]);

  const nameRequired = !isEdit && classMode === 'COHORT';
  const previewName = subject ? deriveClassName(subject.name, liveRows) : '';
  // Before a subject/weekday exists to derive from, "Để trống sẽ hiện {{}}"
  // has nothing to show — the plain example ("VD: Toán 8 tối T3-T5") reads
  // better than a placeholder that names itself.
  const namePlaceholder = previewName
    ? t('classForm.namePlaceholderDerived', { preview: previewName })
    : t('classForm.namePlaceholderCohort');

  // ── Row editing ──────────────────────────────────────────────────────
  function toggleDay(d: number) {
    clearError(); markDirty();
    setRows((prev) => {
      const existing = prev[d];
      if (existing) {
        if (isEdit && existing.id) return { ...prev, [d]: { ...existing, removed: !existing.removed } };
        const next = { ...prev };
        delete next[d];
        return next;
      }
      return {
        ...prev,
        [d]: {
          dayOfWeek: d, startTime: commonStartTime, durationMin: commonDurationMin,
          teacherId: topTeacherId || defaultTeacherId, removed: false,
        },
      };
    });
  }

  function updateRow(d: number, patch: Partial<RowState>) {
    clearError(); markDirty();
    setRows((prev) => (prev[d] ? { ...prev, [d]: { ...prev[d]!, ...patch } } : prev));
  }

  function setCommonTime(startTime: string) {
    clearError(); markDirty();
    setCommonStartTime(startTime);
    setRows((prev) => {
      const next: Rows = {};
      for (const [k, r] of Object.entries(prev)) next[Number(k)] = r!.removed ? r : { ...r!, startTime };
      return next;
    });
  }
  function setCommonDuration(durationMin: string) {
    clearError(); markDirty();
    setCommonDurationMin(durationMin);
    setRows((prev) => {
      const next: Rows = {};
      for (const [k, r] of Object.entries(prev)) next[Number(k)] = r!.removed ? r : { ...r!, durationMin };
      return next;
    });
  }
  function setTopTeacher(id: string) {
    clearError(); markDirty();
    setTopTeacherId(id);
    if (!disclosureOpen) {
      setRows((prev) => {
        const next: Rows = {};
        for (const [k, r] of Object.entries(prev)) next[Number(k)] = r!.removed || r!.id ? r : { ...r!, teacherId: id };
        return next;
      });
    }
  }

  function memberCountFor(slotId: string | undefined): number {
    if (!slotId || !detail) return 0;
    return detail.roster.filter((r) => r.status !== 'ENDED' && r.slotIds.includes(slotId)).length;
  }

  // ── Submit ───────────────────────────────────────────────────────────
  const create = useCreateClass();
  const update = useUpdateClass(classId ?? '');
  const isPending = create.isPending || update.isPending;

  const isTermActive = pricingChoice === 'SESSION_TERM';
  const termOk = !isTermActive || termSize.trim().length > 0;
  const canSubmit = liveRows.length > 0 && (!nameRequired || name.trim().length > 0) && (isEdit || !!subjectId) && termOk && !formError;

  // Fix wave (review I3): `room` and `defaults.*` now seed for real from
  // `detail` (the edit-seed block above), so the form's own state IS the
  // current value the moment it opens — create and edit build the exact
  // same payload from it, no more "only if touched" gate. Sending the
  // whole active pricing selection together (pricingType + billingPeriod +
  // rate + chargeAbsent) keeps it self-consistent even when only one of
  // them changed.
  function buildDefaults(): ClassDefaultsInput | undefined {
    const out: ClassDefaultsInput = {};
    out.pricingType = pricingChoice === 'MONTHLY_FLAT' ? 'MONTHLY_FLAT' : 'PER_SESSION';
    out.billingPeriod = pricingChoice === 'MONTHLY_FLAT' ? undefined : pricingChoice === 'SESSION_TERM' ? 'TERM' : 'MONTHLY';
    if (pricingChoice === 'SESSION_TERM' && termSize.trim()) out.termSize = Number(termSize);
    if (rate.trim()) {
      if (pricingChoice === 'MONTHLY_FLAT') out.monthlyRate = Number(rate);
      else out.ratePerSession = Number(rate);
    }
    if (pricingChoice !== 'MONTHLY_FLAT') out.chargeAbsent = chargeAbsent;
    return out;
  }

  function buildSlots(): ClassSlotRowInput[] {
    return tableDays.flatMap((d) => {
      const row = rows[d]!;
      if (row.removed) return [];
      const out: ClassSlotRowInput = {
        dayOfWeek: d, startTime: row.startTime, durationMin: Number(row.durationMin),
        ...(row.id ? { id: row.id } : {}),
      };
      // teacherId: only for a row without an id — an existing row's teacher
      // is immutable (ruling 2) and simply omitted (server leaves it alone).
      if (!row.id && showTeacher && row.teacherId) out.teacherId = row.teacherId;
      // branchId: only when the org has the field AND the user touched it —
      // an untouched field must not collapse genuinely different per-slot
      // branches down to one guess (see the component's own header comment
      // on the room/defaults gap this mirrors).
      if (showBranchField && branchTouched) out.branchId = branchId || null;
      return [out];
    });
  }

  function handleError(err: Error & { code?: string; dayOfWeek?: number; startTime?: string }) {
    if (err.code === 'SLOT_TIME_CONFLICT' && err.dayOfWeek != null && err.startTime) {
      setDisclosureOpen(true);
      setFormError({ message: err.message, target: { kind: 'row', dayOfWeek: err.dayOfWeek, startTime: err.startTime } });
      return;
    }
    if (err.code === 'START_DATE_IN_RECONCILED_MONTH') {
      setFormError({ message: err.message, target: { kind: 'startsOn' } });
      return;
    }
    if (err.code === 'SLOT_TEACHER_IMMUTABLE' || err.code === 'SLOT_NOT_ACTIVE') {
      setFormError({ message: err.message, target: { kind: 'generic' } });
      toast.error(err.message);
      return;
    }
    // Fix wave (review M5) — "Đang tải…" (a loading string) as the
    // catch-all error fallback; now a real generic error string.
    toast.error(err.message || t('classForm.genericError'));
  }

  function submit() {
    const slots = buildSlots();
    if (!isEdit) {
      const payload: CreateClassInput = {
        subjectId, name: name.trim() || null, room: room.trim() || null,
        maxCapacity: maxCapacity ? Number(maxCapacity) : null,
        startsOn, endsOn: hasEndDate && endsOn ? endsOn : null,
        color, defaults: buildDefaults(), slots,
      };
      create.mutate(payload, {
        onSuccess: (created) => { onOpenChange(false); onSaved?.(created); },
        onError: handleError,
      });
      return;
    }
    const payload: UpdateClassInput = {
      name: name.trim() || null,
      room: room.trim() || null,
      maxCapacity: maxCapacity ? Number(maxCapacity) : null,
      startsOn, endsOn: hasEndDate && endsOn ? endsOn : null,
      color,
      defaults: buildDefaults(),
      slots,
    };
    update.mutate(payload, {
      onSuccess: (updated) => { onOpenChange(false); onSaved?.(updated); },
      onError: handleError,
    });
  }

  // ── Copy ─────────────────────────────────────────────────────────────
  const title = isEdit ? (
    <span>{t('classForm.editTitle')} <span className="text-surface-500 font-medium">· {detail?.displayName ?? ''}</span></span>
  ) : t('classForm.createTitle');
  const description = isEdit ? t('classForm.editDescription') : (isCollapsed ? undefined : t('classForm.createDescriptionCohort'));

  const footer = (
    <div className="flex shrink-0 items-center justify-end gap-2 border-t border-surface-200 bg-surface-0 px-6 py-3 pb-[calc(12.8px+env(safe-area-inset-bottom))]">
      <FooterCancelButton disabled={isPending} label={t('classForm.cancel')} />
      <Button type="button" className="h-[33.6px] md:h-[33.6px] px-4" onClick={submit} disabled={isPending || !canSubmit}>
        {isEdit ? t('classForm.submitEdit') : t('classForm.submitCreate')}
      </Button>
    </div>
  );

  const teacherSelectDot = (opt: { color: string; name: string }) => (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-2 w-2 rounded-full" style={{ background: opt.color }} />
      {opt.name}
    </span>
  );

  return (
    <ResponsiveSheet
      open={open}
      onOpenChange={onOpenChange}
      a11yTitle={t('classForm.a11yTitle')}
      title={title}
      description={description}
      desktopWidth={520}
      dirty={dirty}
      discard={{
        title: t('classForm.discardTitle'), body: t('classForm.discardBody'),
        confirm: t('classForm.discardConfirm'), cancel: t('classForm.discardCancel'),
      }}
      footer={footer}
    >
      {!detailReady ? (
        <div className="flex min-h-[200px] flex-1 items-center justify-center text-sm text-surface-500">{t('classForm.loading')}</div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-4">
          {/* ── Section 1: Thông tin lớp ─────────────────────────────── */}
          <section className="flex flex-col gap-[11.2px]">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-surface-500">{t('classForm.sectionInfo')}</div>

            {isCollapsed ? (
              <>
                <div className="grid grid-cols-2 gap-[11.2px]">
                  <label className="flex flex-col gap-1.5">
                    <span className="text-[13px] font-medium text-surface-700">{t('classForm.subject')}</span>
                    <Select value={subjectId} onValueChange={(v) => { clearError(); markDirty(); setSubjectId(v); }}>
                      <SelectTrigger className="h-[44px] md:h-[33.6px]"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{(subjects ?? []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </label>
                  {showTeacher ? (
                    <label className="flex flex-col gap-1.5">
                      <span className="text-[13px] font-medium text-surface-700">{t('classForm.teacherResponsible')}</span>
                      <Select value={topTeacherId || defaultTeacherId} onValueChange={setTopTeacher}>
                        <SelectTrigger className="h-[44px] md:h-[33.6px]"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>{teacherOptions.map((opt) => <SelectItem key={opt.id} value={opt.id}>{teacherSelectDot(opt)}</SelectItem>)}</SelectContent>
                      </Select>
                    </label>
                  ) : null}
                </div>
                <label className="flex flex-col gap-1.5">
                  <span className="text-[13px] font-medium text-surface-700">{t('classForm.nameOptional')}</span>
                  <Input
                    className="h-[44px] md:h-[33.6px]"
                    value={name}
                    onChange={(e) => { clearError(); markDirty(); setName(e.target.value); }}
                    placeholder={namePlaceholder}
                  />
                </label>
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[11.2px]">
                <label className="md:col-span-2 flex flex-col gap-1.5">
                  <span className="text-[13px] font-medium text-surface-700">
                    {nameRequired ? t('classForm.name') : t('classForm.nameOptional')}
                  </span>
                  <Input
                    className="h-[44px] md:h-[33.6px]"
                    value={name}
                    onChange={(e) => { clearError(); markDirty(); setName(e.target.value); }}
                    placeholder={nameRequired ? t('classForm.namePlaceholderCohort') : namePlaceholder}
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="flex items-center gap-1 text-[13px] font-medium text-surface-700">
                    {t('classForm.subject')}
                    {isEdit ? <Lock className="h-[13px] w-[13px] opacity-60" strokeWidth={1.8} /> : null}
                  </span>
                  <Select value={subjectId || detail?.subject.id} onValueChange={(v) => { clearError(); markDirty(); setSubjectId(v); }} disabled={isEdit}>
                    <SelectTrigger className="h-[44px] md:h-[33.6px]"><SelectValue placeholder="—">{isEdit ? detail?.subject.name : undefined}</SelectValue></SelectTrigger>
                    <SelectContent>{(subjects ?? []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </label>
                {!isEdit && showTeacher ? (
                  <label className="flex flex-col gap-1.5">
                    <span className="text-[13px] font-medium text-surface-700">{t('classForm.teacher')}</span>
                    <Select value={topTeacherId || defaultTeacherId} onValueChange={setTopTeacher}>
                      <SelectTrigger className="h-[44px] md:h-[33.6px]"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{teacherOptions.map((opt) => <SelectItem key={opt.id} value={opt.id}>{teacherSelectDot(opt)}</SelectItem>)}</SelectContent>
                    </Select>
                  </label>
                ) : null}
                {showBranchField ? (
                  <label className="flex flex-col gap-1.5">
                    <span className="text-[13px] font-medium text-surface-700">{t('classForm.branch')}</span>
                    <Select
                      value={branchId || NO_BRANCH}
                      onValueChange={(v) => { clearError(); markDirty(); setBranchTouched(true); setBranchId(v === NO_BRANCH ? '' : v); }}
                    >
                      <SelectTrigger className="h-[44px] md:h-[33.6px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_BRANCH}>{t('classForm.branchNone')}</SelectItem>
                        {activeBranches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </label>
                ) : null}
                <label className="flex flex-col gap-1.5">
                  <span className="text-[13px] font-medium text-surface-700">{t('classForm.room')}</span>
                  <Input
                    className="h-[44px] md:h-[33.6px]"
                    value={room}
                    onChange={(e) => { clearError(); markDirty(); setRoom(e.target.value); }}
                    placeholder={isEdit ? t('classForm.roomUnchangedHint') : undefined}
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-[13px] font-medium text-surface-700">{t('classForm.capacity')}</span>
                  <Input
                    type="number" min="1" max="100"
                    className="h-[44px] md:h-[33.6px] tabular"
                    value={maxCapacity}
                    onChange={(e) => { clearError(); markDirty(); setMaxCapacity(e.target.value); }}
                    placeholder="∞"
                  />
                  <span className="text-[12px] text-surface-500">{t('classForm.capacityHint')}</span>
                </label>
              </div>
            )}
          </section>

          {/* ── Section 2: Lịch học hàng tuần ────────────────────────── */}
          <section className="flex flex-col gap-[11.2px]">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-surface-500">{t('classForm.sectionSchedule')}</div>
            <div className="flex gap-[4.8px] md:gap-[6.4px]">
              {WEEKDAYS.map((d) => {
                const row = rows[d];
                const on = !!row && !row.removed;
                const softRemoved = !!row?.removed;
                return (
                  <button
                    key={d}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleDay(d)}
                    className={cn(
                      'flex-1 h-[44px] md:h-[36px] rounded-lg border text-[13px] font-semibold transition-colors',
                      on && 'border-brand-500 bg-brand-500 text-white',
                      !on && !softRemoved && 'border-surface-200 bg-surface-0 text-surface-700',
                      softRemoved && 'border-attention-border bg-attention-tint text-attention-foreground',
                    )}
                  >
                    {t(`days.${d}`)}
                  </button>
                );
              })}
            </div>

            {!disclosureOpen ? (
              <div className={cn('grid gap-[9.6px] md:gap-[11.2px]', isCollapsed ? 'grid-cols-3' : 'grid-cols-2')}>
                <label className="flex flex-col gap-1.5">
                  <span className="text-[13px] font-medium text-surface-700">{t('classForm.startTime')}</span>
                  <Input type="time" className="h-[44px] md:h-[33.6px] tabular" value={commonStartTime} onChange={(e) => setCommonTime(e.target.value)} />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-[13px] font-medium text-surface-700">{t('classForm.duration')}</span>
                  <Input type="number" min="15" step="15" className="h-[44px] md:h-[33.6px] tabular" value={commonDurationMin} onChange={(e) => setCommonDuration(e.target.value)} />
                </label>
                {/* INDIVIDUAL create keeps Sĩ số inline with the schedule row
                    (S4/2's own 3-col grid) rather than folding it into the
                    pricing/colour summary — COHORT keeps it in section 1. */}
                {isCollapsed ? (
                  <label className="flex flex-col gap-1.5">
                    <span className="text-[13px] font-medium text-surface-700">{t('classForm.capacity')}</span>
                    <Input
                      type="number" min="1" max="100"
                      className="h-[44px] md:h-[33.6px] tabular"
                      value={maxCapacity}
                      onChange={(e) => { markDirty(); setMaxCapacity(e.target.value); }}
                      placeholder="∞"
                    />
                  </label>
                ) : null}
              </div>
            ) : null}

            <button
              type="button"
              onClick={() => setDisclosureOpen((v) => !v)}
              className="inline-flex min-h-[32px] items-center gap-1.5 self-start text-[13px] font-medium text-brand-600"
            >
              {disclosureOpen ? <ChevronDown className="h-[14px] w-[14px]" /> : <ChevronRight className="h-[14px] w-[14px]" />}
              {t('classForm.perDayToggle')}
            </button>

            {disclosureOpen && tableDays.length > 0 ? (
              <div className="rounded-lg border border-surface-200 overflow-hidden">
                <div className="grid grid-cols-[44px_1fr_1fr_1.4fr_28px] gap-2 items-center px-[9.6px] py-[6.4px] text-[11px] font-semibold uppercase tracking-wide text-surface-500 border-b border-surface-100">
                  <span>{t('classForm.perDayDay')}</span>
                  <span>{t('classForm.perDayHour')}</span>
                  <span>{t('classForm.perDayMinute')}</span>
                  <span>{t('classForm.perDayTeacher')}</span>
                  <span />
                </div>
                {tableDays.map((d) => {
                  const row = rows[d]!;
                  const rowConflict = formError?.target.kind === 'row' && formError.target.dayOfWeek === d && formError.target.startTime === row.startTime;
                  const teacherResolved = resolveTeacher(membersInclRemoved ?? [], row.teacherId);
                  return (
                    <div
                      key={d}
                      className={cn(
                        'grid grid-cols-[44px_1fr_1fr_1.4fr_28px] gap-2 items-center px-[9.6px] py-[6.4px] border-b border-surface-100 last:border-b-0',
                        row.removed && 'bg-surface-100',
                      )}
                    >
                      <span className={cn('text-[13px] font-semibold', row.removed && 'text-surface-400 line-through')}>{t(`days.${d}`)}</span>
                      {row.removed ? (
                        <span className="col-span-3 flex items-start gap-1.5 text-[12.5px] text-attention-foreground">
                          {t('classForm.removeDayConsequence', { day: t(`days.${d}`), time: row.startTime, count: memberCountFor(row.id) })}
                        </span>
                      ) : (
                        <>
                          <Input
                            type="time"
                            className={cn('h-[30.4px] px-2 text-[13px] tabular', rowConflict && 'border-destructive ring-2 ring-destructive/15')}
                            value={row.startTime}
                            onChange={(e) => updateRow(d, { startTime: e.target.value })}
                          />
                          <Input
                            type="number" min="15" step="15"
                            className="h-[30.4px] px-2 text-[13px] tabular"
                            value={row.durationMin}
                            onChange={(e) => updateRow(d, { durationMin: e.target.value })}
                          />
                          {row.id ? (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className={cn(
                                    'flex h-[30.4px] items-center justify-between gap-1.5 rounded-md border px-2 text-[13px] text-surface-600',
                                    rowConflict ? 'border-destructive ring-2 ring-destructive/15' : 'border-surface-200 bg-surface-100',
                                  )}>
                                    {teacherResolved ? teacherSelectDot(teacherResolved) : '—'}
                                    <Lock className="h-[12px] w-[12px] opacity-60" strokeWidth={1.8} />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-[240px] text-xs leading-snug">{t('classForm.teacherLockedTooltip')}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <Select value={row.teacherId} onValueChange={(v) => updateRow(d, { teacherId: v })}>
                              <SelectTrigger className={cn('h-[30.4px] px-2 text-[13px]', rowConflict && 'border-destructive ring-2 ring-destructive/15')}>
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>{teacherOptions.map((opt) => <SelectItem key={opt.id} value={opt.id}>{teacherSelectDot(opt)}</SelectItem>)}</SelectContent>
                            </Select>
                          )}
                          <span />
                        </>
                      )}
                      {row.removed ? (
                        <button type="button" aria-label={t('classForm.undo')} onClick={() => toggleDay(d)} className="flex h-[28px] w-[28px] items-center justify-center rounded-md text-brand-600">
                          <Undo2 className="h-[14px] w-[14px]" />
                        </button>
                      ) : null}
                      {rowConflict ? (
                        <p className="col-span-4 col-start-2 m-0 flex items-start gap-1.5 text-[12.5px] text-destructive">
                          <AlertCircle className="h-[13px] w-[13px] mt-[3px] shrink-0" />
                          {formError.message}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </section>

          {/* ── Section 3: Ngày bắt đầu / kết thúc ───────────────────── */}
          <section className="grid grid-cols-2 gap-[9.6px] md:gap-[11.2px]">
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium text-surface-700">{t('classForm.startsOn')}</span>
              <DatePicker
                value={startsOn}
                onChange={(v) => { clearError(); markDirty(); setStartsOn(v); }}
                className={cn('h-[44px] md:h-[33.6px] w-full justify-between font-normal', formError?.target.kind === 'startsOn' && 'border-destructive ring-2 ring-destructive/15')}
              />
              {formError?.target.kind === 'startsOn' ? (
                <span className="flex items-start gap-1.5 text-[12px] text-destructive">
                  <AlertCircle className="h-[13px] w-[13px] mt-[2px] shrink-0" />
                  {formError.message}
                </span>
              ) : (
                <span className="text-[12px] text-surface-500">{t('classForm.startsOnHint')}</span>
              )}
            </label>
            {isCollapsed && !hasEndDate ? (
              <button
                type="button"
                onClick={() => { markDirty(); setHasEndDate(true); }}
                className="mt-[22px] inline-flex h-[44px] md:h-[33.6px] items-center gap-1.5 self-start text-[13px] font-medium text-brand-600"
              >
                <Plus className="h-[14px] w-[14px]" />
                {t('classForm.addEndDate')}
              </button>
            ) : (
              <label className="flex flex-col gap-1.5">
                <span className="text-[13px] font-medium text-surface-700">
                  {t('classForm.endsOn')} <span className="text-surface-500 font-normal">{t('classForm.endsOnOptional')}</span>
                </span>
                <DatePicker
                  value={endsOn}
                  onChange={(v) => { markDirty(); setEndsOn(v); }}
                  clearable
                  className="h-[44px] md:h-[33.6px] w-full justify-between font-normal"
                />
                <span className="text-[12px] text-surface-500">{isEdit ? t('classForm.endsOnHintEdit') : t('classForm.endsOnHintCreate')}</span>
              </label>
            )}
          </section>

          {/* ── Sections 4+5: Học phí mặc định / Màu trên lịch ───────── */}
          {isCollapsed && !pricingExpanded ? (
            <button
              type="button"
              onClick={() => setPricingExpanded(true)}
              className="flex w-full items-center justify-between rounded-lg border border-surface-200 bg-surface-50 px-[12.8px] py-[9.6px] text-left"
            >
              <span>
                <span className="block text-[13.5px] font-medium text-surface-700">{t('classForm.pricingColorSummaryLabel')}</span>
                <span className="block text-[12px] text-surface-500">
                  {[
                    t(`classForm.pricing${pricingChoice === 'SESSION_MONTHLY' ? 'SessionMonthly' : pricingChoice === 'SESSION_TERM' ? 'SessionTerm' : 'MonthlyFlat'}Label`),
                    rate.trim() ? formatVnd(rate) : (subject?.defaultRate != null ? t('classForm.rateFromSubject', { rate: formatVnd(subject.defaultRate) }) : null),
                    color ? t('classForm.colorChosen') : t('classForm.colorAuto'),
                  ].filter(Boolean).join(' · ')}
                </span>
              </span>
              <ChevronDown className="h-[16px] w-[16px] text-surface-500" />
            </button>
          ) : (
            <>
              <section className="flex flex-col gap-[11.2px]">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-surface-500">{t('classForm.sectionPricing')}</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {PRICING_CHOICES.map((choice) => {
                    const selected = pricingChoice === choice;
                    const labelKey = choice === 'SESSION_MONTHLY' ? 'pricingSessionMonthlyLabel' : choice === 'SESSION_TERM' ? 'pricingSessionTermLabel' : 'pricingMonthlyFlatLabel';
                    const descKey = choice === 'SESSION_MONTHLY' ? 'pricingSessionMonthlyDesc' : choice === 'SESSION_TERM' ? 'pricingSessionTermDesc' : 'pricingMonthlyFlatDesc';
                    return (
                      <button
                        key={choice}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => { clearError(); markDirty(); setPricingChoice(choice); }}
                        className={cn(
                          'text-left rounded-lg border px-[11.2px] py-[9.6px]',
                          selected ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500' : 'border-surface-200 bg-surface-0',
                        )}
                      >
                        <span className="block text-[13.5px] font-semibold text-surface-800">{t(`classForm.${labelKey}`)}</span>
                        <span className="block text-[12px] leading-snug text-surface-500">{t(`classForm.${descKey}`)}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-[11.2px] items-end">
                  <label className="flex flex-col gap-1.5">
                    <span className="text-[13px] font-medium text-surface-700">
                      {pricingChoice === 'MONTHLY_FLAT' ? t('classForm.monthlyRate') : t('classForm.ratePerSession')}
                    </span>
                    <Input
                      type="number" min="0"
                      className="h-[44px] md:h-[33.6px] tabular"
                      value={rate}
                      onChange={(e) => { markDirty(); setRate(e.target.value); }}
                      placeholder={subject ? String((pricingChoice === 'MONTHLY_FLAT' ? subject.defaultMonthlyRate : subject.defaultRate) ?? '') : undefined}
                    />
                  </label>
                  {pricingChoice === 'SESSION_TERM' ? (
                    <label className="flex flex-col gap-1.5">
                      <span className="text-[13px] font-medium text-surface-700">{t('classForm.termSize')}</span>
                      <Input
                        type="number" min="1"
                        className="h-[44px] md:h-[33.6px] tabular"
                        value={termSize}
                        onChange={(e) => { markDirty(); setTermSize(e.target.value); }}
                      />
                    </label>
                  ) : null}
                </div>
                {pricingChoice !== 'MONTHLY_FLAT' ? (
                  <label className="flex items-center gap-2 text-[13px] text-surface-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={chargeAbsent}
                      onChange={(e) => { markDirty(); setChargeAbsentState(e.target.checked); }}
                    />
                    {t('classForm.chargeAbsent')}
                  </label>
                ) : null}
                <p className="m-0 text-[12px] text-surface-500">{t('classForm.pricingHint')}</p>
              </section>

              <section className="flex items-center gap-[12.8px]">
                <div className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-surface-500">
                  {t('classForm.sectionColor')}
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild><Info className="h-[13px] w-[13px]" strokeWidth={1.8} /></TooltipTrigger>
                      <TooltipContent className="max-w-[240px] text-xs leading-snug">{t('classForm.colorTooltip')}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <button
                  type="button"
                  aria-pressed={color === null}
                  onClick={() => { markDirty(); setColor(null); }}
                  className={cn(
                    'h-[26px] rounded-full border px-[9.6px] text-[12.5px] font-semibold',
                    color === null ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-surface-200 text-surface-600',
                  )}
                >
                  {t('classForm.colorAuto')}
                </button>
                <div className="flex gap-[6.4px]">
                  {CLASS_COLORS.map((c, i) => {
                    const style = classColorStyle(c)!;
                    return (
                      <button
                        key={c}
                        type="button"
                        aria-label={t('classForm.colorSwatchLabel', { n: i + 1 })}
                        aria-pressed={color === c}
                        onClick={() => { markDirty(); setColor(c); }}
                        className={cn('h-[22px] w-[22px] rounded-full border-2', color === c ? 'ring-2 ring-offset-1 ring-brand-500' : '')}
                        style={{ background: style.background, borderColor: style.borderColor }}
                      />
                    );
                  })}
                </div>
              </section>
            </>
          )}
        </div>
      )}
    </ResponsiveSheet>
  );
}
